import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { addMoney, compareMoney, moneyFromMajorFloat } from "@performyard/money";
import { type ColDef, type TableRef } from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";

import { areFieldsVisible } from "../../../../components/Link/Cards/Employee/utils";
import { MyDecisionsGroups } from "./MyDecisionsGroups";

import { DecisionRow } from "../../types";

import {
	useCompanyFields,
	useCompanySalaryCustomFieldData,
	useCompanySalaryFieldId,
	useCurrentUser,
	useMyDecisionDetails,
	useRbacPrimaryRolePermissions,
} from "../../../../api/hooks";
import { type CompensationDecisionNotesLeavePayload } from "../../../../hooks/useDecisionNotesAutosave";
import { useDecisionNotes } from "./hooks/useDecisionNotes";
import { useDecisionValidation } from "./hooks/useDecisionValidation";
import { useIncreaseValues } from "./hooks/useIncreaseValues";

import ErrorPage from "../../../Error";
import { useCompensationAccess } from "../../useCompensationAccess";
import {
	CurrentSalaryCellRenderer,
	EmployeeInfoCellRenderer,
	EmployeeNameCellRenderer,
	IncreaseAmountCellRenderer,
	IncreasePercentCellRenderer,
	NewSalaryCellRenderer,
	SuggestedIncreaseCellRenderer,
	SuperRowRenderer,
	newSalaryValueGetter,
	notesPayloadKey,
	toDecisionRow,
} from "../CellRenderers";

export type { CompensationDecisionNotesLeavePayload };

const myDecisionsColumnDefs: ColDef<DecisionRow>[] = [
	{
		headerName: "Employee",
		field: "employee_name",
		cellRenderer: EmployeeNameCellRenderer,
		width: 200,
		flex: 1.5,
		sortable: true,
	},
	{
		headerName: "Current Salary",
		field: "current_salary",
		cellRenderer: CurrentSalaryCellRenderer,
		flex: 1,
		sortable: true,
		comparator: compareMoney,
	},
	{
		headerName: "Suggested",
		flex: 0.8,
		sortable: false,
		cellRenderer: SuggestedIncreaseCellRenderer,
	},
	{
		headerName: "Increase",
		field: "proposed_increase_percent",
		cellRenderer: IncreasePercentCellRenderer,
		flex: 1,
		sortable: false,
	},
	{
		headerName: "Increase",
		field: "proposed_increase_amount",
		cellRenderer: IncreaseAmountCellRenderer,
		flex: 1,
		sortable: false,
	},
	{
		headerName: "New Salary",
		field: "proposed_salary",
		valueGetter: newSalaryValueGetter,
		cellRenderer: NewSalaryCellRenderer,
		flex: 1,
		sortable: true,
		comparator: compareMoney,
	},
	{
		headerName: "Employee Info",
		cellRenderer: EmployeeInfoCellRenderer,
		sortable: false,
		resizable: false,
	},
];

const MyDecisions: React.FC = () => {
	const { cycleId } = useParams<{ cycleId: string }>();
	const {
		userID,
		isLoading: isAccessLoading,
		compensationDisabled,
		canMakeDecisions,
		canLoadMyDecisions,
		canAlwaysViewSalary,
	} = useCompensationAccess(cycleId);
	const {
		data: myDecisionsData,
		isLoading: isLoadingMyDecisions,
		isError: isMyDecisionsError,
		isPending: isMyDecisionsPending,
	} = useMyDecisionDetails(userID ?? "", cycleId ?? "", {
		enabled: canLoadMyDecisions,
	});
	const { data: salaryFieldData } = useCompanySalaryFieldId();
	const salaryFieldId = salaryFieldData?.salaryCustomFieldId;
	const { data: salaryCustomFieldData, isLoading: isLoadingSalaryField } = useCompanySalaryCustomFieldData();
	const managersExcludedFromSalaryHistory =
		!isLoadingSalaryField &&
		!salaryCustomFieldData?.salaryCustomField?.visibility?.includes("manager") &&
		!canAlwaysViewSalary;
	const hasDecisionsToMake =
		!isLoadingMyDecisions &&
		!isMyDecisionsPending &&
		(myDecisionsData?.directReportsToMakeDecisionsFor?.results?.length ?? 0) > 0;
	const { data: currentUser } = useCurrentUser();
	const companyId = currentUser?.me?.authorization?.payload?.company_id;
	const { data: companyFields } = useCompanyFields(companyId || "");
	const { data: employeeDetailsPermissions } = useRbacPrimaryRolePermissions("employee_details");
	const fieldVisibility = useMemo(() => {
		// Reaching this page requires `canMakeDecisions`, which is granted to managers for these rows.
		const permissions = ["all", "manager"];
		if (employeeDetailsPermissions?.includes("employee_details:viewAdminField")) {
			permissions.push("admin");
		}
		const result = areFieldsVisible(
			[
				{ name: "jobTitle", lookup: "Job Title" },
				{ name: "role", lookup: "Role" },
			],
			companyFields,
			permissions
		);
		return { canViewJobTitle: !!result.jobTitle, canViewRole: !!result.role };
	}, [companyFields, employeeDetailsPermissions]);
	const { compensationCycleDetails: cycleDetails, directReportsToMakeDecisionsFor: myDecisionsDetails } =
		myDecisionsData ?? {};

	const [expandedRowIdsByGroup, setExpandedRowIdsByGroup] = useState<Record<string, string | null>>({});
	const gridRefs = useRef<Record<string, TableRef<DecisionRow>>>({});

	let cycleStatus: string = cycleDetails?.status || "";
	if (cycleDetails?.status === "active" && new Date(cycleDetails?.startDate) > new Date()) {
		cycleStatus = "scheduled";
	}

	const groupedByManagerId = useMemo(() => {
		const results = myDecisionsDetails?.results ?? [];
		type GroupedResults = Record<string, typeof results>;

		const grouped = results.reduce((acc: GroupedResults, user) => {
			const managerId = String(user.managerId ?? "");
			if (!managerId) return acc;

			acc[managerId] ??= [];
			acc[managerId].push(user);
			return acc;
		}, {});

		return Object.entries(grouped as GroupedResults).map(([managerId, users]) => ({
			managerId,
			users,
		}));
	}, [myDecisionsDetails?.results]);

	const employeeIdToManagerId = useMemo(() => {
		const map = new Map<string, string>();
		for (const g of groupedByManagerId) {
			for (const u of g.users) {
				map.set(String(u.employeeId), g.managerId);
			}
		}
		return map;
	}, [groupedByManagerId]);

	const baseDecisionRowsByManagerId = useMemo(
		() =>
			Object.fromEntries(
				groupedByManagerId.map((group) => [
					group.managerId,
					group.users.map((dr, index) => toDecisionRow(dr, index, userID)),
				])
			) as Record<string, DecisionRow[]>,
		[groupedByManagerId, userID]
	);

	const baseDecisionRowsFlat = useMemo(
		() => Object.values(baseDecisionRowsByManagerId).flat(),
		[baseDecisionRowsByManagerId]
	);

	// --- Custom hooks ---

	const { increaseValues, setDraftOverrides, onPercentChange, onAmountChange } =
		useIncreaseValues(baseDecisionRowsByManagerId);

	const { notesValuesRef, activeNoteEditorRef, onNotesChange } = useDecisionNotes({
		baseDecisionRowsFlat,
		employeeIdToManagerId,
		gridRefs,
		cycleId,
		userID,
	});

	const onExpand = useCallback((groupId: string, rowId: string) => {
		setExpandedRowIdsByGroup((prev) => ({ ...prev, [groupId]: rowId }));
	}, []);

	const onCollapse = useCallback((groupId: string) => {
		setExpandedRowIdsByGroup((prev) => ({ ...prev, [groupId]: null }));
	}, []);

	const groupDecisionRowsByManagerId = useMemo(() => {
		return Object.fromEntries(
			Object.entries(baseDecisionRowsByManagerId).map(([managerId, rows]) => {
				const rowsWithIncreaseValues = rows.map((row) => {
					const draft = increaseValues[row._id];
					const proposedIncreasePercent =
						draft?.percent === "" ? null : (draft?.percent ?? row.proposed_increase_percent ?? null);
					// A draft amount is a user-typed major-unit number and becomes exact money
					// here; an unedited row already holds `Money`.
					const proposedIncreaseAmount =
						draft?.amount === ""
							? null
							: draft?.amount != null
								? moneyFromMajorFloat(draft.amount)
								: (row.proposed_increase_amount ?? null);

					return {
						...row,
						proposed_increase_percent: proposedIncreasePercent,
						proposed_increase_amount: proposedIncreaseAmount,
						// Summed exactly. One field, so the displayed salary and the submitted
						// salary cannot drift apart.
						proposed_salary:
							proposedIncreaseAmount != null
								? addMoney(row.current_salary, proposedIncreaseAmount)
								: row.proposed_salary,
						notes: notesValuesRef.current[row.employee_id] ?? row.notes ?? "",
					};
				});

				return [managerId, rowsWithIncreaseValues];
			})
		) as Record<string, DecisionRow[]>;
	}, [baseDecisionRowsByManagerId, increaseValues]);

	const { groupCannotBeSubmitted, refreshingGroupIds, validateSubmitDecisions } = useDecisionValidation({
		cycleId,
		userID,
		groupDecisionRowsByManagerId,
		setDraftOverrides,
	});

	const rawAllotment = cycleDetails?.allotmentIncreasePercent;
	const allotmentPercent = typeof rawAllotment === "number" && Number.isFinite(rawAllotment) ? rawAllotment : 0;
	const recommendedIncreasePercent = allotmentPercent;
	const baseGridContext = useMemo(
		() => ({
			increaseValues,
			recommendedIncreasePercent,
			onPercentChange,
			onAmountChange,
			notesValuesRef,
			onNotesChange,
			groupCannotBeSubmitted: groupCannotBeSubmitted ?? null,
			activeNoteEditorRef,
			canViewRole: fieldVisibility.canViewRole,
			canViewJobTitle: fieldVisibility.canViewJobTitle,
			salaryFieldId,
			managersExcludedFromSalaryHistory,
		}),
		[
			increaseValues,
			recommendedIncreasePercent,
			onPercentChange,
			onAmountChange,
			onNotesChange,
			groupCannotBeSubmitted,
			fieldVisibility.canViewRole,
			fieldVisibility.canViewJobTitle,
			salaryFieldId,
			managersExcludedFromSalaryHistory,
		]
	);

	// Refresh cells when draft values, validation flag, or expansion change
	// (AG Grid can keep stale `context` on React renderers until refresh).
	useEffect(() => {
		Object.values(gridRefs.current).forEach((grid) => {
			const api = grid?.api;
			if (!api) return;
			api.refreshCells({ force: true });
		});
	}, [increaseValues, groupCannotBeSubmitted, expandedRowIdsByGroup]);

	// Fast permission/feature-flag checks first — no need to wait for slow data fetches
	if (isAccessLoading) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (compensationDisabled || !canMakeDecisions) {
		return <ErrorPage code={403}></ErrorPage>;
	}

	// Data-dependent checks — only reached after permissions are confirmed
	if (canLoadMyDecisions && isMyDecisionsPending) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (canLoadMyDecisions && isMyDecisionsError && myDecisionsData == null) {
		return <ErrorPage code={404} />;
	}

	if (cycleStatus !== "active") {
		return <ErrorPage code={403}></ErrorPage>;
	}

	if (!hasDecisionsToMake) {
		return <ErrorPage code={403}></ErrorPage>;
	}
	return (
		<MyDecisionsGroups
			groupedByManagerId={groupedByManagerId}
			refreshingGroupIds={refreshingGroupIds}
			userID={userID}
			expandedRowIdsByGroup={expandedRowIdsByGroup}
			baseGridContext={baseGridContext}
			onExpand={onExpand}
			onCollapse={onCollapse}
			recommendedIncreasePercent={recommendedIncreasePercent}
			increaseValues={increaseValues}
			handleSubmitDecisions={validateSubmitDecisions}
			gridRefs={gridRefs}
			columnDefs={myDecisionsColumnDefs}
			notesPayloadKey={notesPayloadKey}
			fullWidthCellRenderer={SuperRowRenderer}
			groupCannotBeSubmitted={groupCannotBeSubmitted}
			cycleStatus={cycleStatus}
		/>
	);
};

export default MyDecisions;
